import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Settings,
  Shield,
  Mail,
  Building,
  Save,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Globe,
  FileText,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';
import { useApp } from '@/context/AppContext';

export default function ParametresSysteme() {
  const { currentUser } = useApp();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // System settings
  const [settings, setSettings] = useState({
    // Company info
    company_name: 'Jeld-Wen France SAS',
    company_address: 'Zone Industrielle, 32800 Eauze',
    
    // RGPD / DPO
    dpo_email: 'dpo@jeldwen.com',
    dpo_name: '',
    dpo_phone: '',
    
    // Privacy policy
    privacy_policy_version: '1.0',
    privacy_policy_last_update: new Date().toISOString().split('T')[0],
    
    // Data retention (in years)
    retention_autorisations: 5,
    retention_caces: 5,
    retention_formations: 5,
    retention_sante: 10,
    retention_logs: 1,
    
    // Notifications
    notification_email_enabled: true,
    sender_email: 'noreply@jeldwen.com'
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await api.getSystemSettings();
      if (data) {
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.log('No existing settings, using defaults');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(settings.dpo_email)) {
      toast.error('Format d\'email DPO invalide');
      return;
    }
    
    try {
      setSaving(true);
      await api.updateSystemSettings(settings);
      toast.success('Paramètres enregistrés avec succès');
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Settings className="h-6 w-6" />
          Paramètres Système
        </h1>
        <p className="text-gray-500">Configuration générale et paramètres RGPD</p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Company Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5 text-blue-600" />
              Informations de l'entreprise
            </CardTitle>
            <CardDescription>
              Ces informations apparaissent dans la politique de confidentialité
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Nom de l'entreprise</Label>
                <Input
                  id="company_name"
                  value={settings.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  placeholder="Jeld-Wen France SAS"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_address">Adresse</Label>
                <Input
                  id="company_address"
                  value={settings.company_address}
                  onChange={(e) => handleChange('company_address', e.target.value)}
                  placeholder="Zone Industrielle, 32800 Eauze"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* DPO / RGPD Settings */}
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <Shield className="h-5 w-5" />
              Paramètres RGPD / DPO
            </CardTitle>
            <CardDescription>
              Configuration du Délégué à la Protection des Données
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-blue-50 border-blue-200">
              <Shield className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                Ces informations sont affichées dans la politique de confidentialité et utilisées pour les demandes RGPD.
              </AlertDescription>
            </Alert>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dpo_email" className="flex items-center gap-1">
                  <Mail className="h-4 w-4" />
                  Email du DPO *
                </Label>
                <Input
                  id="dpo_email"
                  type="email"
                  value={settings.dpo_email}
                  onChange={(e) => handleChange('dpo_email', e.target.value)}
                  placeholder="dpo@entreprise.com"
                  required
                />
                <p className="text-xs text-gray-500">
                  Cet email recevra les demandes de suppression et autres requêtes RGPD
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="dpo_name">Nom du DPO</Label>
                <Input
                  id="dpo_name"
                  value={settings.dpo_name}
                  onChange={(e) => handleChange('dpo_name', e.target.value)}
                  placeholder="Jean Dupont"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dpo_phone">Téléphone du DPO</Label>
                <Input
                  id="dpo_phone"
                  value={settings.dpo_phone}
                  onChange={(e) => handleChange('dpo_phone', e.target.value)}
                  placeholder="+33 5 62 XX XX XX"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Privacy Policy Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-green-600" />
              Politique de Confidentialité
            </CardTitle>
            <CardDescription>
              Versionnage et suivi des mises à jour
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="privacy_policy_version">Version</Label>
                <Input
                  id="privacy_policy_version"
                  value={settings.privacy_policy_version}
                  onChange={(e) => handleChange('privacy_policy_version', e.target.value)}
                  placeholder="1.0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="privacy_policy_last_update">Date de dernière mise à jour</Label>
                <Input
                  id="privacy_policy_last_update"
                  type="date"
                  value={settings.privacy_policy_last_update}
                  onChange={(e) => handleChange('privacy_policy_last_update', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Retention */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              Durées de Conservation
            </CardTitle>
            <CardDescription>
              Conformément au Code du travail et aux obligations légales
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="retention_autorisations">Autorisations de conduite</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="retention_autorisations"
                    type="number"
                    min="1"
                    max="20"
                    value={settings.retention_autorisations}
                    onChange={(e) => handleChange('retention_autorisations', parseInt(e.target.value))}
                    className="w-20"
                  />
                  <span className="text-sm text-gray-500">ans après expiration</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="retention_caces">Certificats CACES</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="retention_caces"
                    type="number"
                    min="1"
                    max="20"
                    value={settings.retention_caces}
                    onChange={(e) => handleChange('retention_caces', parseInt(e.target.value))}
                    className="w-20"
                  />
                  <span className="text-sm text-gray-500">ans après expiration</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="retention_formations">Attestations de formation</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="retention_formations"
                    type="number"
                    min="1"
                    max="20"
                    value={settings.retention_formations}
                    onChange={(e) => handleChange('retention_formations', parseInt(e.target.value))}
                    className="w-20"
                  />
                  <span className="text-sm text-gray-500">ans après fin contrat</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="retention_sante">Données de santé</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="retention_sante"
                    type="number"
                    min="5"
                    max="50"
                    value={settings.retention_sante}
                    onChange={(e) => handleChange('retention_sante', parseInt(e.target.value))}
                    className="w-20"
                  />
                  <span className="text-sm text-gray-500">ans (prescription)</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="retention_logs">Logs de connexion</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="retention_logs"
                    type="number"
                    min="1"
                    max="5"
                    value={settings.retention_logs}
                    onChange={(e) => handleChange('retention_logs', parseInt(e.target.value))}
                    className="w-20"
                  />
                  <span className="text-sm text-gray-500">an(s)</span>
                </div>
              </div>
            </div>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Ces durées sont indicatives. Les données peuvent être conservées plus longtemps en cas de contentieux ou d'obligation légale spécifique.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Email Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-purple-600" />
              Paramètres Email
            </CardTitle>
            <CardDescription>
              Configuration des notifications par email
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sender_email">Email expéditeur</Label>
                <Input
                  id="sender_email"
                  type="email"
                  value={settings.sender_email}
                  onChange={(e) => handleChange('sender_email', e.target.value)}
                  placeholder="noreply@entreprise.com"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={loadSettings} disabled={saving}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Réinitialiser
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Enregistrer les paramètres
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
